import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Add as AddIcon,
  AccountTree as DiagramIcon,
  CalendarToday as DateIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSpace } from '../contexts/SpaceContext';
import diagramsService from '../services/diagrams.service';

export const DiagramsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [diagramToDelete, setDiagramToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Fetch diagrams for current space
  const {
    data: diagrams = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['diagrams', currentSpace?.id],
    queryFn: () => diagramsService.getAll(currentSpace?.id ?? ''),
    enabled: !!currentSpace?.id,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => diagramsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['diagrams', currentSpace?.id],
      });
      setDeleteDialogOpen(false);
      setDiagramToDelete(null);
    },
  });

  const handleDeleteClick = (diagram: { id: string; name: string }) => {
    setDiagramToDelete(diagram);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (diagramToDelete) {
      deleteMutation.mutate(diagramToDelete.id);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DiagramIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            {t('nav.diagrams')}
          </Typography>
        </Box>
        {currentSpace && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() =>
              navigate(`/spaces/${currentSpace.id}?action=create-diagram`)
            }
          >
            {t('diagram.create')}
          </Button>
        )}
      </Box>

      {!currentSpace ? (
        <Paper sx={{ p: 3 }}>
          <Alert severity="info">{t('space.selectToView')}</Alert>
        </Paper>
      ) : isLoading ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('common.loading')}
          </Typography>
        </Paper>
      ) : error ? (
        <Paper sx={{ p: 3 }}>
          <Alert severity="error">
            {t('common.error')}: {(error as Error).message}
          </Alert>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          {diagrams.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
              }}
            >
              <DiagramIcon
                sx={{
                  fontSize: 80,
                  color: 'text.disabled',
                  mb: 2,
                }}
              />
              <Typography variant="h6" gutterBottom color="text.secondary">
                {t('diagram.noDiagrams')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('diagram.noDiagramsDescription')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() =>
                  navigate(`/spaces/${currentSpace.id}?action=create-diagram`)
                }
              >
                {t('diagram.createFirst')}
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {diagrams.map(diagram => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={diagram.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 150,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      {diagram.thumbnail ? (
                        <Box
                          component="img"
                          src={`data:image/png;base64,${diagram.thumbnail}`}
                          alt={diagram.name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <DiagramIcon
                          sx={{ fontSize: 60, color: 'text.disabled' }}
                        />
                      )}
                    </Box>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom noWrap>
                        {diagram.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {diagram.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip
                          label={`${diagram.diagramData.nodes.length} ${t('diagram.components')}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${diagram.diagramData.edges.length} ${t('diagram.relations')}`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mt: 1,
                        }}
                      >
                        <DateIcon
                          sx={{ fontSize: 14, color: 'text.secondary' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {t('diagram.updated')}:{' '}
                          {new Date(diagram.updatedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </CardContent>
                    <CardActions
                      sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}
                    >
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          onClick={() =>
                            navigate(
                              `/spaces/${currentSpace?.id}/diagrams/${diagram.id}`
                            )
                          }
                        >
                          {t('common.edit')}
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            handleDeleteClick({
                              id: diagram.id,
                              name: diagram.name,
                            })
                          }
                          title={t('common.delete')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() =>
                          navigate(
                            `/spaces/${currentSpace?.id}/diagrams/${diagram.id}`
                          )
                        }
                      >
                        {t('common.open')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('diagram.deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('diagram.deleteConfirm.message', {
              name: diagramToDelete?.name,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('common.delete')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
